import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Chip, Divider, Link, Stack, Typography } from '@mui/material';
import { PageSection } from '@/components/Layout/PageLayout';
import theme from '@/theme';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useDesktopDownloads } from '@/hooks/useDesktopDownloads';
import { useDesktopConnection } from '@/hooks/useDesktopConnection';
import Loading from '@/components/Loading';
import { detectDesktopPlatform, DesktopPlatform } from '@/utils/deviceDetection';
import { useAuthStore } from '@/stores/authStore';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import type { DesktopDownloads } from '@/services/desktopDownloadService';
import { compareVersions, isValidVersion } from '@/utils/versionGate';
import DesktopHandoffPreference from './DesktopHandoffPreference';

type DownloadField = keyof DesktopDownloads;
// The platform uninstallers are beta-only links, not builds: neither has a
// stable counterpart or a place in the platform ordering, so they stay out of
// the build maps and are rendered on their own.
type BuildField = Exclude<DownloadField, 'mac_uninstall_beta_url' | 'windows_uninstall_beta_url' | 'latest_version'>;
type BetaDownloadField = Extract<BuildField, `${string}_beta_url`>;
type StableDownloadField = Exclude<BuildField, BetaDownloadField>;

interface DownloadOption {
  field: BuildField;
  label: string;
  url: string;
}

// Beta builds reuse the platform label — the section they render in is what
// marks them as beta, so repeating it on every button would just be noise.
const BUILD_LABELS: Record<BuildField, string> = {
  mac_arm_url: 'macOS (Apple Silicon)',
  mac_intel_url: 'macOS (Intel)',
  windows_intel_url: 'Windows (Intel)',
  windows_arm_url: 'Windows (ARM)',
  mac_arm_beta_url: 'macOS (Apple Silicon)',
  mac_intel_beta_url: 'macOS (Intel)',
  windows_intel_beta_url: 'Windows (Intel)',
  windows_arm_beta_url: 'Windows (ARM)',
};

/** The pre-release build that corresponds to each stable build. */
const BETA_FIELD_BY_BUILD: Record<StableDownloadField, BetaDownloadField> = {
  mac_arm_url: 'mac_arm_beta_url',
  mac_intel_url: 'mac_intel_beta_url',
  windows_intel_url: 'windows_intel_beta_url',
  windows_arm_url: 'windows_arm_beta_url',
};

/**
 * Build order for the detected platform. The first entry is the recommended
 * build; the rest are secondary. `equalWeight` means the architecture is
 * undetermined, so every listed build renders with the same emphasis.
 */
const buildOrderFor = ({ os, arch }: DesktopPlatform): { fields: StableDownloadField[]; equalWeight: boolean } => {
  if (os === 'mac') {
    if (arch === 'arm') return { fields: ['mac_arm_url', 'mac_intel_url'], equalWeight: false };
    if (arch === 'intel') return { fields: ['mac_intel_url', 'mac_arm_url'], equalWeight: false };
    return { fields: ['mac_arm_url', 'mac_intel_url'], equalWeight: true };
  }

  if (os === 'windows') {
    if (arch === 'arm') return { fields: ['windows_arm_url', 'windows_intel_url'], equalWeight: false };
    if (arch === 'intel') return { fields: ['windows_intel_url', 'windows_arm_url'], equalWeight: false };
    return { fields: ['windows_intel_url', 'windows_arm_url'], equalWeight: true };
  }

  return {
    fields: ['mac_arm_url', 'mac_intel_url', 'windows_intel_url', 'windows_arm_url'],
    equalWeight: true,
  };
};

/**
 * Downloads open in a new window so the app stays put behind them. `noopener`
 * only — `noreferrer` would strip the Referer header, which breaks the download
 * outright if the CDN referer-gates these URLs.
 */
const NEW_WINDOW_PROPS = { target: '_blank', rel: 'noopener' } as const;

const ALLOWED_DOWNLOAD_PROTOCOLS = ['https:'];

/** Rejects anything but an absolute https URL — e.g. a hand-entered `javascript:` scheme. */
const isSafeDownloadUrl = (url: string): boolean => {
  try {
    return ALLOWED_DOWNLOAD_PROTOCOLS.includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

/**
 * Resolve a raw doc field to a usable href, or '' when unconfigured. An empty
 * or absent field means that link is unavailable and must not be rendered. The
 * doc is hand-edited in the Firestore console, so a field typed as anything
 * other than a safe https string is treated as unconfigured rather than allowed
 * to throw or render as an executable `javascript:` href.
 */
const configuredUrl = (raw: unknown): string => {
  const url = typeof raw === 'string' ? raw.trim() : '';
  return url && isSafeDownloadUrl(url) ? url : '';
};

/**
 * Resolve a raw version field to a usable version, or '' when unusable. Same
 * defensiveness as `configuredUrl`: the doc is hand-edited in the Firestore
 * console, so a non-string or malformed value is treated as "not recorded"
 * rather than rendered. Never `.trim()` a raw field before the type check.
 */
const configuredVersion = (raw: unknown): string => {
  const value = typeof raw === 'string' ? raw.trim() : '';
  return value && isValidVersion(value) ? value : '';
};

interface VersionStatus {
  severity: 'success' | 'info' | 'warning';
  title: string;
  body: string;
}

interface VersionCheck {
  installedVersion: string;
  latestVersion: string;
  detectedVersion: string | null;
  isUpToDate: boolean;
  isUpdateAvailable: boolean;
  isNewer: boolean;
}

/**
 * The status banner for a finished check, or null when no desktop was detected.
 */
const describeVersionStatus = ({
  installedVersion,
  latestVersion,
  detectedVersion,
  isUpToDate,
  isUpdateAvailable,
  isNewer,
}: VersionCheck): VersionStatus | null => {
  if (isUpToDate) {
    return {
      severity: 'success',
      title: 'You’re up to date',
      body: `NoteGo v${installedVersion} on this computer is the latest stable release.`,
    };
  }

  if (isUpdateAvailable) {
    return {
      severity: 'info',
      title: 'Update available',
      body: `This computer is running NoteGo v${installedVersion}. The latest stable release is v${latestVersion} — download it below.`,
    };
  }

  if (isNewer) {
    return {
      severity: 'info',
      title: 'You’re ahead of the stable release',
      body: `This computer is running NoteGo v${installedVersion}. The latest stable download is v${latestVersion}.`,
    };
  }

  if (detectedVersion !== null) {
    return {
      severity: 'warning',
      title: `Detected NoteGo v${detectedVersion}`,
      body: 'We couldn’t verify a matching latest release.',
    };
  }

  return null;
};

/** Drop builds with no URL configured. */
const availableBuilds = (fields: BuildField[], downloads: DesktopDownloads): DownloadOption[] =>
  fields.reduce<DownloadOption[]>((options, field) => {
    const url = configuredUrl(downloads[field]);
    if (url) {
      options.push({ field, label: BUILD_LABELS[field], url });
    }
    return options;
  }, []);

/**
 * Renders one group of build buttons. `showRecommended` promotes the first
 * entry to the primary call to action and demotes the rest to text links.
 */
const renderBuilds = (builds: DownloadOption[], showRecommended: boolean, primaryVariant: 'contained' | 'outlined') => {
  if (!showRecommended) {
    return (
      <Stack flexDirection="row" flexWrap="wrap" gap="1rem">
        {builds.map((option) => (
          <Button key={option.field} variant={primaryVariant} color="primary" href={option.url} {...NEW_WINDOW_PROPS}>
            Download for {option.label}
          </Button>
        ))}
      </Stack>
    );
  }

  const [recommended, ...alternates] = builds;

  return (
    <Stack gap="1rem" alignItems="flex-start">
      <Button variant={primaryVariant} color="primary" href={recommended.url} {...NEW_WINDOW_PROPS}>
        Download for {recommended.label}
      </Button>

      {alternates.length > 0 && (
        <Stack gap="0.25rem" alignItems="flex-start">
          {alternates.map((option) => (
            <Button key={option.field} variant="text" size="small" href={option.url} {...NEW_WINDOW_PROPS}>
              Download for {option.label}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

/** Holds the Download card's place while loading, so nothing jumps when it fills in. */
const DesktopAppLoading = () => (
  <PageSection title="Download">
    <Stack alignItems="center" py={4}>
      <Loading />
    </Stack>
  </PageSection>
);

const DesktopApp = () => {
  const { userData } = useAuthStore();
  const { features, isLoading: featuresLoading } = useCurrentPlanFeatures();
  const { downloads, feedVersions, loading, refresh } = useDesktopDownloads();
  const connection = useDesktopConnection(!featuresLoading && features.desktopDownload === true);
  const [platform, setPlatform] = useState<DesktopPlatform>({ os: 'unknown', arch: 'unknown' });

  useEffect(() => {
    let cancelled = false;

    detectDesktopPlatform().then((detected) => {
      if (!cancelled) {
        setPlatform(detected);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Both loads must settle before deciding. `usePricingData` has no shared
  // cache, so this component starts its own pricing fetch that races the
  // downloads fetch — without `featuresLoading` an entitled user sees the
  // unavailable message flash before the buttons appear.
  if (loading || featuresLoading) {
    return <DesktopAppLoading />;
  }

  const detected = connection.desktop;
  const installedPlatform: DesktopPlatform = detected
    ? {
        os: detected.platform === 'darwin' ? 'mac' : detected.platform === 'win32' ? 'windows' : 'unknown',
        arch: detected.appArch === 'arm64' ? 'arm' : ['x64', 'ia32'].includes(detected.appArch) ? 'intel' : 'unknown',
      }
    : platform;
  const { fields, equalWeight } = buildOrderFor(installedPlatform);
  const options = downloads ? availableBuilds(fields, downloads) : [];

  // Beta builds are rendered only for legacy BETA-plan testers (no feature checkbox
  // covers beta builds, so a single granted beta feature doesn't unlock them), in the same platform order
  // as the stable builds. This is presentation only, not secrecy: the whole
  // app_downloads/current doc — beta URLs included — is fetched client-side by
  // every entitled user, so treat these URLs as public and never put anything
  // behind one that a non-tester must not reach.
  const isBetaTester = hasBetaProductName(userData);
  const betaOptions =
    isBetaTester && downloads
      ? availableBuilds(
          fields.map((field) => BETA_FIELD_BY_BUILD[field]),
          downloads,
        )
      : [];

  // The platform uninstallers are offered to beta testers alongside the beta
  // builds, each hidden on the other OS; an undetected OS still shows both,
  // matching how the builds themselves fall back to showing everything.
  const macUninstallUrl =
    isBetaTester && downloads && platform.os !== 'windows' ? configuredUrl(downloads.mac_uninstall_beta_url) : '';
  const windowsUninstallUrl =
    isBetaTester && downloads && platform.os !== 'mac' ? configuredUrl(downloads.windows_uninstall_beta_url) : '';

  // Nothing to offer: the plan lookup failed, the Firestore doc is missing, or
  // no build is configured for this platform. This is a route of its own now,
  // so say so — rendering null would leave the page blank with no explanation.
  if (features.desktopDownload !== true || (options.length === 0 && betaOptions.length === 0)) {
    return (
      <>
        <PageSection title="Download">
          <Typography variant="body2" color={theme.palette.neutral.greyFont}>
            The desktop app isn&apos;t available for download right now. Please check back shortly.
          </Typography>
        </PageSection>

        {/* Nothing to download does not mean nothing installed — someone who
            chose "never" must still be able to undo it from here. */}
        <DesktopHandoffPreference />
      </>
    );
  }

  const showRecommended = !equalWeight;

  // Only a live response identifies this computer. The account's desktopVersion
  // can belong to a different machine and is never used for this decision.
  const installedVersion = configuredVersion(detected?.version);
  // The update feed for this computer's build is what its updater is actually
  // offered, and it is per platform — Mac and Windows ship on different days. The
  // admin-entered latest_version only stands in when the feed couldn't be read.
  const latestVersion = configuredVersion(feedVersions[fields[0]]) || configuredVersion(downloads?.latest_version);
  const betaVersion = configuredVersion(feedVersions[BETA_FIELD_BY_BUILD[fields[0]]]);
  const hasMatchingDownload =
    !!detected &&
    detected.platform !== 'linux' &&
    ['arm64', 'x64'].includes(detected.appArch) &&
    options.some((option) => option.field === fields[0]);
  const hasBothVersions = hasMatchingDownload && installedVersion !== '' && latestVersion !== '';
  const isUpToDate = hasBothVersions && compareVersions(installedVersion, latestVersion) === 0;
  const isNewer = hasBothVersions && compareVersions(installedVersion, latestVersion) > 0;
  const isUpdateAvailable = hasBothVersions && compareVersions(installedVersion, latestVersion) < 0;

  const versionStatus = describeVersionStatus({
    installedVersion,
    latestVersion,
    detectedVersion: detected ? detected.version : null,
    isUpToDate,
    isUpdateAvailable,
    isNewer,
  });

  const isChecking = connection.phase === 'checking' || connection.phase === 'waiting';
  // A check that couldn't reach NoteGo shows nothing at all — the old "couldn't
  // verify" warning confused people into filing support tickets.
  const showConnectionStatus = connection.phase !== 'unknown';

  // Already current (or ahead of stable): the stable download would only
  // reinstall the same or an older build, so the whole group is dropped rather
  // than demoted. The beta group is a different build and stays.
  const showStableDownloads = options.length > 0 && !isUpToDate && !isNewer;

  // The beta list can be missing the recommended architecture even when the
  // stable list has it, and promoting whatever happens to be first would put a
  // primary "Download" CTA on the wrong arch. Only lead the beta group when it
  // actually starts with the build we'd recommend.
  const showRecommendedBeta = showRecommended && betaOptions[0]?.field === BETA_FIELD_BY_BUILD[fields[0]];

  return (
    <>
      <PageSection title="Download">
        <Stack gap="1.5rem">
          {showConnectionStatus && (
            <Stack gap="1rem" alignItems="flex-start" aria-live="polite">
              {isChecking ? (
                <Typography variant="body2">
                  {connection.phase === 'checking'
                    ? 'Checking NoteGo on this computer…'
                    : 'Waiting for NoteGo. Approve the browser’s Open NoteGo prompt to finish checking.'}
                </Typography>
              ) : versionStatus ? (
                <Alert severity={versionStatus.severity} sx={{ alignSelf: 'stretch' }}>
                  <AlertTitle>{versionStatus.title}</AlertTitle>
                  {versionStatus.body}
                </Alert>
              ) : (
                <Typography variant="body2">
                  Open NoteGo to check the version on this computer. You can also download it below.
                </Typography>
              )}

              {!isChecking && (
                <Stack flexDirection="row" flexWrap="wrap" gap="1rem">
                  {connection.launchUrl && connection.phase === 'ready' && (
                    <Button variant="contained" href={connection.launchUrl} onClick={connection.openDesktop}>
                      Open NoteGo to check
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      refresh();
                      void connection.recheck();
                    }}
                  >
                    Check again
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {showStableDownloads && (
            <>
              <Typography variant="body2" color={theme.palette.neutral.greyFont}>
                {showRecommended
                  ? `Download the NoteGo desktop app for ${options[0].label}. Not the right one? Pick another build below.`
                  : 'Download the NoteGo desktop app for your computer. Choose the build that matches your machine.'}
              </Typography>

              {renderBuilds(options, showRecommended, 'contained')}
            </>
          )}

          {betaOptions.length > 0 && (
            <>
              <Divider />

              <Stack gap="0.75rem" alignItems="flex-start">
                <Chip
                  label="Beta"
                  size="small"
                  sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 500 }}
                />

                <Typography variant="body2" color={theme.palette.neutral.greyFont}>
                  Pre-release build{betaVersion ? ` v${betaVersion}` : ''}, available to you as a beta tester. Expect
                  rough edges — install it alongside the stable app and tell us what breaks.
                </Typography>

                {renderBuilds(betaOptions, showRecommendedBeta, options.length > 0 ? 'outlined' : 'contained')}

                {macUninstallUrl !== '' && (
                  <Button variant="text" size="small" href={macUninstallUrl} {...NEW_WINDOW_PROPS}>
                    Uninstall NoteGo for macOS
                  </Button>
                )}

                {windowsUninstallUrl !== '' && (
                  <Button variant="text" size="small" href={windowsUninstallUrl} {...NEW_WINDOW_PROPS}>
                    Uninstall NoteGo for Windows
                  </Button>
                )}
              </Stack>
            </>
          )}

          <Typography variant="body2" color={theme.palette.neutral.greyFont}>
            Need help installing?{' '}
            <Link href="https://notego.ai/2026/10/14/install-notego-desktop-app/" {...NEW_WINDOW_PROPS}>
              Read more here
            </Link>
          </Typography>
        </Stack>
      </PageSection>

      <DesktopHandoffPreference />
    </>
  );
};

export default DesktopApp;
